import React, { createContext, useContext, useState, useEffect, useRef, useCallback, ReactNode } from 'react';
import { authAPI, superadminAPI, settOrgKontekstHeader, User, OrgKontekst, MeResponse } from '../services/api';
import { queryClient } from '../lib/queryClient';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateUser: (updatedUser: User) => void;
  isAuthenticated: boolean;
  isAdmin: () => boolean;
  hasRole: (role: string) => boolean;
  /** Valgt organisasjon (kun superadmin). Serveren eier konteksten. */
  orgKontekst: OrgKontekst | null;
  /** Konteksten er endret (annen fane, 409/428). Siden må lastes på nytt. */
  kontekstUtdatert: boolean;
  velgOrgKontekst: (id: number) => Promise<void>;
  avsluttOrgKontekst: () => Promise<void>;
}

const KONTEKST_KANAL = 'voluplan-org-kontekst';

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [orgKontekst, setOrgKontekst] = useState<OrgKontekst | null>(null);
  const [kontekstUtdatert, setKontekstUtdatert] = useState(false);
  const kanalRef = useRef<BroadcastChannel | null>(null);
  const orgKontekstIdRef = useRef<number | null>(null);

  const erSuperadmin = user?.roles?.includes('superadmin') ?? false;

  // State, ref og header holdes samlet i sync.
  const settKontekst = useCallback((kontekst: OrgKontekst | null) => {
    orgKontekstIdRef.current = kontekst?.id ?? null;
    setOrgKontekst(kontekst);
    settOrgKontekstHeader(kontekst?.id ?? null);
  }, []);

  // Bruker og kontekst kommer alltid sammen fra me-svaret. Bare superadmin har kontekst.
  const brukMeSvar = useCallback((response: MeResponse) => {
    setUser(response.user);
    const superadmin = response.user.roles?.includes('superadmin') ?? false;
    settKontekst(superadmin ? (response.orgKontekst ?? null) : null);
  }, [settKontekst]);

  useEffect(() => {
    // Ny kontekst tas aldri stille i bruk i en åpen side: vi markerer bare siden som utdatert.
    const merkUtdatert = () => setKontekstUtdatert(true);
    window.addEventListener('org-kontekst-ugyldig', merkUtdatert);
    return () => window.removeEventListener('org-kontekst-ugyldig', merkUtdatert);
  }, []);

  useEffect(() => {
    // Én kanal per superadmin-fane: varsler andre faner når konteksten byttes.
    if (!erSuperadmin || typeof BroadcastChannel === 'undefined') return undefined;
    const kanal = new BroadcastChannel(KONTEKST_KANAL);
    kanal.onmessage = (event: MessageEvent) => {
      if (event.data?.id !== orgKontekstIdRef.current) {
        setKontekstUtdatert(true);
      }
    };
    kanalRef.current = kanal;
    return () => {
      kanal.close();
      kanalRef.current = null;
    };
  }, [erSuperadmin]);

  useEffect(() => {
    // Check if user is already logged in via httpOnly cookie
    const initAuth = async () => {
      // Ikke sjekk session på offentlige sider (unngå unødvendige 401 errors)
      const publicRoutes = ['/login', '/forgot-password', '/reset-password', '/auth/callback'];
      const currentPath = window.location.pathname;
      const isPublicRoute = publicRoutes.some(route => currentPath.startsWith(route));
      
      if (isPublicRoute) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await authAPI.getCurrentUser();
        brukMeSvar(response);
      } catch (error) {
        // No valid session cookie, user not authenticated
        setUser(null);
        settKontekst(null);
      } finally {
        setLoading(false);
      }
    };

    initAuth();
  }, [brukMeSvar, settKontekst]);

  const login = async (email: string, password: string) => {
    const response = await authAPI.login({ email, password });
    // Token is automatically stored in httpOnly cookie by backend
    // Tøm cachen fra en ev. forrige bruker (samme fane) før vi setter ny bruker
    queryClient.clear();
    setUser(response.user);
    // Login sletter org-kontekst-cookien på serveren: ny innlogging starter uten kontekst.
    settKontekst(null);
    setKontekstUtdatert(false);
  };

  const logout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Tøm cachen slik at neste bruker i samme fane ikke arver denne brukerens data
      queryClient.clear();
      setUser(null);
      settKontekst(null);
      setKontekstUtdatert(false);
      // Cookie is cleared by backend
    }
  };

  const refreshUser = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      brukMeSvar(response);
    } catch (error) {
      console.error('Refresh user error:', error);
      // If refresh fails, user is probably not authenticated anymore
      setUser(null);
      settKontekst(null);
    }
  };

  const updateUser = (updatedUser: User) => {
    setUser(updatedUser);
  };

  const velgOrgKontekst = async (id: number) => {
    const response = await superadminAPI.velgKontekst(id);
    settKontekst(response.orgKontekst);
    setKontekstUtdatert(false);
    queryClient.clear();
    kanalRef.current?.postMessage({ id: response.orgKontekst.id });
  };

  const avsluttOrgKontekst = async () => {
    await superadminAPI.avsluttKontekst();
    settKontekst(null);
    setKontekstUtdatert(false);
    queryClient.clear();
    kanalRef.current?.postMessage({ id: null });
  };

  // Superadmin har bare org-rettigheter (som admin) når en kontekst er valgt.
  const isAdmin = () => {
    return (user?.roles?.includes('admin') ?? false) || (erSuperadmin && orgKontekst !== null);
  };

  const hasRole = (role: string) => {
    return user?.roles?.includes(role) ?? false;
  };

  const value = {
    user,
    loading,
    login,
    logout,
    refreshUser,
    updateUser,
    isAuthenticated: !!user,
    isAdmin,
    hasRole,
    orgKontekst,
    kontekstUtdatert,
    velgOrgKontekst,
    avsluttOrgKontekst,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

